<template>
  <div class="similar-tools-container max-w-7xl">
    <div class="card-style rounded-xl p-4 sm:p-6 group hover:shadow-lg hover:shadow-blue-500/5 transition-all">
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-2">
          <div class="p-1.5 rounded-lg bg-yellow-500/10">
            <Icon icon="heroicons:sparkles" class="w-5 h-5 text-yellow-400" />
          </div>
          <h2 class="text-lg font-medium text-white">Similar Tools</h2>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="py-8">
        <div class="flex flex-col items-center justify-center space-y-4">
          <div class="loader-dots flex space-x-2">
            <div class="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
            <div class="w-3 h-3 bg-blue-500 rounded-full animate-pulse" style="animation-delay: 0.2s"></div>
            <div class="w-3 h-3 bg-blue-500 rounded-full animate-pulse" style="animation-delay: 0.4s"></div>
          </div>
          <p class="text-gray-400 text-sm">Loading similar tools...</p>
        </div>
      </div>

      <!-- No Similar Tools State -->
      <div v-else-if="!relatedTools || relatedTools.length === 0" class="text-center py-8">
        <div class="inline-flex mx-auto items-center justify-center w-16 h-16 rounded-full bg-gray-800/50 mb-4">
          <Icon icon="heroicons:magnifying-glass" class="w-8 h-8 text-gray-500" />
        </div>
        <p class="text-gray-400">No similar tools found at this time.</p>
      </div>

      <!-- Tools Grid with Similarity Score -->
      <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
        <Link
          v-for="tool in relatedTools"
          :key="tool.id"
          :href="`/tool/${tool.slug}`"
          class="group/card relative bg-gray-800/30 hover:bg-gray-800/50 rounded-xl overflow-hidden transition-all duration-300 flex flex-col"
        >
          <!-- Similarity Badge (if score available) -->
          <div 
            v-if="tool.similarity_score" 
            class="absolute top-2 right-2 bg-blue-500/80 text-white text-xs px-2 py-0.5 rounded-full"
          >
            {{ Math.round(tool.similarity_score * 100) }}% match
          </div>

          <!-- Card Content -->
          <div class="p-2 sm:p-4 flex-grow flex flex-col">
            <!-- Tool Header -->
            <div class="flex items-start gap-3 mb-2">
              <div class="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gray-700/50 p-1.5 sm:p-2 flex items-center justify-center">
                <img 
                  :src="getToolImageUrl(tool)"
                  :alt="tool.name"
                  class="w-full h-full object-contain"
                  @error="onImageError"
                />
              </div>
              <div class="flex-1 min-w-0">
                <h3 class="font-medium text-sm sm:text-base text-white truncate group-hover/card:text-blue-400 transition-colors">
                  {{ tool.name }}
                </h3>
                <div class="flex items-center gap-3 mt-1 text-sm">
                  <!-- Pricing Models -->
                  <div class="flex items-center gap-1">
                    <Icon icon="heroicons:currency-dollar" class="w-4 h-4 text-blue-400" />
                    <div class="flex flex-wrap items-center gap-x-2">
                      <template v-if="tool.pricing_model">
                        <span class="text-gray-300 text-sm capitalize">{{ tool.pricing_model }}</span>
                      </template>
                      <span v-else class="text-gray-300 text-sm">Free</span>
                    </div>
                  </div>

                  <div class="w-px h-4 bg-gray-700"></div>

                  <!-- Platforms -->
                  <div class="flex items-center gap-1">
                    <Icon icon="heroicons:computer-desktop" class="w-4 h-4 text-purple-400" />
                    <div class="flex flex-wrap items-center gap-x-2">
                      <template v-if="tool.platforms && tool.platforms.length">
                        <span 
                          v-for="(platform, index) in tool.platforms.slice(0, 2)" 
                          :key="platform.id"
                          class="text-gray-300 text-sm"
                        >
                          {{ platform.name }}{{ index < Math.min(tool.platforms.length, 2) - 1 ? ',' : '' }}
                        </span>
                        <span v-if="tool.platforms.length > 2" class="text-gray-400 text-xs">+{{ tool.platforms.length - 2 }}</span>
                      </template>
                      <span v-else class="text-gray-300 text-sm">Web</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Description -->
            <p class="text-[10px] sm:text-sm text-gray-400 mb-2 flex-grow description-clamp">
              {{ tool.short_description }}
            </p>

            <!-- Shared Features/Categories (optional if you want to highlight why they're similar) -->
            <div v-if="tool.similarity_breakdown" class="mt-1">
              <div v-if="getSimilarityHighlight(tool)" class="text-xs text-gray-400 mt-1 border-t border-gray-700/50 pt-1">
                <span class="text-teal-400">Similar in:</span> {{ getSimilarityHighlight(tool) }}
              </div>
            </div>

            <!-- Hover Overlay -->
            <div class="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-transparent opacity-0 group-hover/card:opacity-100 transition-opacity pointer-events-none">
              <div class="absolute bottom-3 right-3 bg-blue-500 rounded-full p-2 transform translate-y-2 group-hover/card:translate-y-0 transition-transform">
                <Icon icon="heroicons:arrow-up-right" class="w-4 h-4 text-white" />
              </div>
            </div>
          </div>

          <!-- Bottom Stats -->
          <div class="border-t border-gray-700/50 px-2 sm:px-4 py-1.5 sm:py-2 bg-gray-800/30 flex items-center justify-between">
            <div class="flex items-center gap-2">
              <div v-if="tool.rating" class="flex items-center gap-0.5">
                <Icon icon="heroicons:star" class="w-4 h-4 text-yellow-400" />
                <span class="text-sm text-gray-400">{{ tool.rating }}</span>
              </div>
              <div class="flex items-center gap-0.5">
                <Icon icon="heroicons:heart" class="w-4 h-4 text-pink-400" />
                <span class="text-sm text-gray-400">{{ tool.favorites_count }}</span>
              </div>
            </div>
          </div>
        </Link>
      </div>

      <!-- Browse More Section -->
      <div class="flex justify-center mt-6">
        <Link 
          href="/tools" 
          class="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-all group px-3 py-1.5 rounded-lg hover:bg-gray-800/50"
        >
          <span class="font-medium">All Tools</span>
          <Icon icon="heroicons:arrow-right" class="w-5 h-5" />
        </Link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Link } from '@inertiajs/vue3';
import { Icon } from '@iconify/vue';

const props = defineProps({
  relatedTools: {
    type: Array,
    required: true
  },
  isLoading: {
    type: Boolean,
    default: false
  }
});

const getToolImageUrl = (tool) => {
  return tool.media?.[0]?.original_url || '/storage/no-logo.png';
};

const onImageError = (event) => {
  event.target.src = '/storage/no-logo.png';
};

// Get key similarity factors to highlight why tools are similar
const getSimilarityHighlight = (tool) => {
  if (!tool.similarity_breakdown) return null;
  
  const breakdown = tool.similarity_breakdown;
  const highlights = [];
  
  // Check each factor, highlight if significant similarity
  if (breakdown.use_cases >= 0.7) highlights.push('Use Cases');
  if (breakdown.features >= 0.7) highlights.push('Features');
  if (breakdown.tags >= 0.7) highlights.push('Categories');
  if (breakdown.platforms >= 0.8) highlights.push('Platforms');
  if (breakdown.pricing_model >= 0.8) highlights.push('Pricing');
  
  return highlights.length > 0 ? highlights.join(', ') : null;
};
</script>

<style scoped>
.card-style {
  background: 
    radial-gradient(
      ellipse at center,
      rgba(30, 41, 59, 0.5) 0%, 
      rgba(17, 24, 39, 0.7) 100%
    );
  backdrop-filter: blur(2px);
}

.description-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
}

.animate-pulse {
  animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>